import { ChatSession, ApiProvider } from '@/types';

/**
 * 存储优化工具类
 * 提供数据压缩、缓存和懒加载功能
 */
export class StorageOptimizer {
  private static readonly CACHE_PREFIX = 'api-conversation-cache-';
  private static readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24小时
  private static readonly MAX_CACHE_SIZE = 10 * 1024 * 1024; // 10MB
  
  /**
   * 压缩会话数据
   */
  static compressSession(session: ChatSession): CompressedSession {
    // 压缩消息内容
    const compressedMessages = session.messages.map(message => ({
      ...message,
      content: this.compressText(message.content),
    }));
    
    return {
      ...session,
      messages: compressedMessages,
      _compressed: true,
      _originalSize: JSON.stringify(session).length,
      _compressedSize: 0, // 将在下面计算
    };
  }
  
  /**
   * 解压会话数据
   */
  static decompressSession(compressedSession: CompressedSession): ChatSession {
    // 解压消息内容
    const decompressedMessages = compressedSession.messages.map(message => ({
      ...message,
      content: this.decompressText(message.content),
    }));
    
    return {
      ...compressedSession,
      messages: decompressedMessages,
    };
  }
  
  /**
   * 压缩文本数据
   */
  static compressText(text: string): string {
    if (!text || text.length < 100) return text;
    
    try {
      // 简单的文本压缩：移除多余空格和换行
      let compressed = text
        .replace(/\s+/g, ' ')
        .replace(/\n\s*\n/g, '\n')
        .trim();
      
      // 如果压缩后大小减少不明显，返回原文本
      if (compressed.length > text.length * 0.8) {
        return text;
      }
      
      return compressed;
    } catch {
      return text;
    }
  }
  
  /**
   * 解压文本数据
   */
  static decompressText(compressedText: string): string {
    // 当前压缩算法是可逆的，所以直接返回
    return compressedText;
  }
  
  /**
   * 批量压缩会话
   */
  static compressSessions(sessions: ChatSession[]): CompressedSession[] {
    return sessions.map(session => this.compressSession(session));
  }
  
  /**
   * 批量解压会话
   */
  static decompressSessions(compressedSessions: CompressedSession[]): ChatSession[] {
    return compressedSessions.map(session => this.decompressSession(session));
  }
  
  /**
   * 计算存储大小
   */
  static calculateStorageSize(data: any): {
    originalSize: string;
    compressedSize: string;
    compressionRatio: number;
    estimatedSavings: string;
  } {
    const originalJson = JSON.stringify(data);
    const originalSize = originalJson.length;
    
    // 尝试压缩
    const compressedData = this.compressSessions(data.sessions || []);
    const compressedJson = JSON.stringify({
      ...data,
      sessions: compressedData,
    });
    const compressedSize = compressedJson.length;
    
    const compressionRatio = originalSize > 0 ? compressedSize / originalSize : 1;
    const savings = originalSize - compressedSize;
    
    const formatSize = (bytes: number): string => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };
    
    return {
      originalSize: formatSize(originalSize),
      compressedSize: formatSize(compressedSize),
      compressionRatio,
      estimatedSavings: formatSize(savings),
    };
  }
  
  /**
   * 缓存数据
   */
  static cacheData(key: string, data: any, expiry?: number): void {
    try {
      const cacheItem = {
        data,
        expiry: Date.now() + (expiry || this.CACHE_EXPIRY),
        timestamp: new Date().toISOString(),
      };
      
      localStorage.setItem(`${this.CACHE_PREFIX}${key}`, JSON.stringify(cacheItem));
      
      // 清理过期缓存
      this.cleanExpiredCache();
    } catch (error) {
      console.warn('缓存数据失败:', error);
    }
  }
  
  /**
   * 获取缓存数据
   */
  static getCachedData<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(`${this.CACHE_PREFIX}${key}`);
      if (!cached) return null;
      
      const cacheItem = JSON.parse(cached);
      
      // 检查是否过期
      if (Date.now() > cacheItem.expiry) {
        localStorage.removeItem(`${this.CACHE_PREFIX}${key}`);
        return null;
      }
      
      return cacheItem.data;
    } catch {
      return null;
    }
  }
  
  /**
   * 清理过期缓存
   */
  static cleanExpiredCache(): void {
    try {
      const keysToRemove: string[] = [];
      let totalSize = 0;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          const cached = localStorage.getItem(key);
          if (cached) {
            totalSize += cached.length;
            
            try {
              const cacheItem = JSON.parse(cached);
              if (Date.now() > cacheItem.expiry) {
                keysToRemove.push(key);
              }
            } catch {
              keysToRemove.push(key);
            }
          }
        }
      }
      
      // 清理过期缓存
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // 如果缓存总大小超过限制，清理最旧的缓存
      if (totalSize > this.MAX_CACHE_SIZE) {
        this.cleanOldestCache();
      }
    } catch (error) {
      console.warn('清理缓存失败:', error);
    }
  }
  
  /**
   * 清理最旧的缓存
   */
  private static cleanOldestCache(): void {
    try {
      const cacheItems: Array<{ key: string; timestamp: string }> = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          const cached = localStorage.getItem(key);
          if (cached) {
            try {
              const cacheItem = JSON.parse(cached);
              cacheItems.push({
                key,
                timestamp: cacheItem.timestamp,
              });
            } catch {
              // 无效数据，直接删除
              localStorage.removeItem(key);
            }
          }
        }
      }
      
      // 按时间排序，删除最旧的一半
      cacheItems.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      const itemsToRemove = Math.floor(cacheItems.length / 2);
      
      for (let i = 0; i < itemsToRemove; i++) {
        localStorage.removeItem(cacheItems[i].key);
      }
    } catch (error) {
      console.warn('清理最旧缓存失败:', error);
    }
  }
  
  /**
   * 分页加载数据
   */
  static paginateData<T>(
    data: T[],
    page: number,
    pageSize: number,
    cacheKey?: string
  ): PaginatedResult<T> {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = data.slice(startIndex, endIndex);
    
    // 缓存当前页数据
    if (cacheKey) {
      this.cacheData(`${cacheKey}-page-${page}`, paginatedItems, 5 * 60 * 1000); // 5分钟
    }
    
    return {
      items: paginatedItems,
      pagination: {
        page,
        pageSize,
        total: data.length,
      },
    };
  }
  
  /**
   * 懒加载数据
   */
  static lazyLoadData<T>(
    data: T[],
    loadedCount: number,
    batchSize: number = 20
  ): { items: T[]; hasMore: boolean; nextIndex: number } {
    const nextIndex = loadedCount;
    const endIndex = Math.min(nextIndex + batchSize, data.length);
    const newItems = data.slice(nextIndex, endIndex);
    
    return {
      items: newItems,
      hasMore: endIndex < data.length,
      nextIndex: endIndex,
    };
  }
  
  /**
   * 优化存储性能
   */
  static optimizeStorage(_providers: ApiProvider[], sessions: ChatSession[]): {
    recommendations: string[];
    actions: string[];
    estimatedSavings: string;
  } {
    const recommendations: string[] = [];
    const actions: string[] = [];
    
    // 分析存储情况
    const totalSessions = sessions.length;
    const totalMessages = sessions.reduce((sum, session) => sum + session.messageCount, 0);
    const sizeInfo = this.calculateStorageSize({ sessions });
    
    // 生成建议
    if (totalSessions > 100) {
      recommendations.push(`您有 ${totalSessions} 个对话记录，建议启用数据压缩`);
      actions.push('启用自动压缩存储');
    }
    
    if (totalMessages > 1000) {
      recommendations.push(`您有 ${totalMessages} 条消息记录，建议定期清理旧数据`);
      actions.push('设置自动清理策略');
    }
    
    if (sizeInfo.compressionRatio < 0.9) {
      recommendations.push(`数据压缩可节省约 ${sizeInfo.estimatedSavings} 存储空间`);
      actions.push('立即压缩存储数据');
    }
    
    if (sessions.some(s => s.messages.length > 50)) {
      recommendations.push('发现包含大量消息的对话，建议启用消息懒加载');
      actions.push('启用消息懒加载功能');
    }
    
    return {
      recommendations,
      actions,
      estimatedSavings: sizeInfo.estimatedSavings,
    };
  }
  
  /**
   * 清理旧数据
   */
  static cleanupOldData(
    sessions: ChatSession[],
    options: {
      maxAgeDays?: number;
      maxSessions?: number;
      keepImportant?: boolean;
    } = {}
  ): {
    sessionsToKeep: ChatSession[];
    sessionsToRemove: ChatSession[];
    removedCount: number;
    keptCount: number;
  } {
    const {
      maxAgeDays = 90,
      maxSessions = 1000,
      keepImportant = true,
    } = options;
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - maxAgeDays * 24 * 60 * 60 * 1000);
    
    // 按重要性排序
    const sortedSessions = [...sessions].sort((a, b) => {
      // 重要标签优先
      const aIsImportant = a.tags.includes('重要') || a.tags.includes('important');
      const bIsImportant = b.tags.includes('重要') || b.tags.includes('important');
      
      if (aIsImportant && !bIsImportant) return -1;
      if (!aIsImportant && bIsImportant) return 1;
      
      // 按时间倒序
      return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
    });
    
    // 确定要保留的会话
    const sessionsToKeep: ChatSession[] = [];
    const sessionsToRemove: ChatSession[] = [];
    
    for (const session of sortedSessions) {
      const sessionDate = new Date(session.lastMessageAt);
      const isOld = sessionDate < cutoffDate;
      const isImportant = keepImportant && (session.tags.includes('重要') || session.tags.includes('important'));
      
      if (sessionsToKeep.length < maxSessions && (!isOld || isImportant)) {
        sessionsToKeep.push(session);
      } else {
        sessionsToRemove.push(session);
      }
    }
    
    return {
      sessionsToKeep,
      sessionsToRemove,
      removedCount: sessionsToRemove.length,
      keptCount: sessionsToKeep.length,
    };
  }
}

// 类型定义
interface CompressedSession extends ChatSession {
  _compressed?: boolean;
  _originalSize?: number;
  _compressedSize?: number;
}

interface PaginatedResult<T> {
  items: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}